import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { clearUser } from '../../../features/user/userSlice';
import DashProfile from './dashtabcomponents/DashProfile';
import Advertise from './dashtabcomponents/Advertise';
import {
  HeartPulse,
  Target,
  Calendar,
  Bell,
  FileText,
  Watch,
  CheckCircle2,
  Circle,
  ChevronRight,
  Stethoscope,
  Clock,
  ShieldCheck,
  AlertTriangle,
} from 'lucide-react';
import config from '../../../config';
import { calculateAge } from '../../../utils/helpers';
import { GOAL_LABELS, defaultStepsForGoal } from '../../../constants/familyPlanningGoals';
// axiosInstance attaches the token and retries once after a silent refresh on a 401.
// Adjust this path if axiosInstance.js lives somewhere else in your project.
import axiosInstance from '../../../utils/axiosInstance';

// ============================================================================
// HELPERS
// ============================================================================

function formatFileSize(bytes) {
  if (!Number.isFinite(bytes)) return '--';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function formatReminderDateTime(d) {
  if (!d) return '';
  return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// Combines appointment_date + appointment_time into one instant and drops
// anything already in the past. If the time can't be parsed, falls back to
// end-of-day on the appointment's date rather than hiding it.
function isAppointmentUpcoming(a) {
  const dateStr = String(a.appointment_date || '').slice(0, 10);
  if (!dateStr) return false;

  const timeStr = (a.appointment_time || '').trim();
  let when = timeStr ? new Date(`${dateStr}T${timeStr}`) : null;
  if (!when || isNaN(when.getTime())) {
    when = timeStr ? new Date(`${dateStr} ${timeStr}`) : null;
  }
  if (!when || isNaN(when.getTime())) {
    when = new Date(`${dateStr}T23:59:59`);
  }

  return when.getTime() >= Date.now();
}

// Latest non-null reading for one metric across date-ascending rows.
// Today's row is often partial (e.g. only movement), so reading just the
// last row gives nulls for heart rate / blood pressure.
function latestMetric(rows, key) {
  for (let i = rows.length - 1; i >= 0; i--) {
    const v = rows[i]?.[key];
    if (v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v))) {
      return { value: Number(v), row: rows[i] };
    }
  }
  return null;
}

const num = (v) => (v !== null && v !== undefined && Number.isFinite(Number(v)) ? Number(v) : null);

// Trajectory values are lowercase strings from the backend. Unknown values fall back to gray.
const TRAJECTORY_COLORS = {
  stable: '#0F4C75',
  optimal: '#0F6E56',
  improving: '#0F6E56',
  strong: '#0F6E56',
  draining: '#B3261E',
  declining: '#B3261E',
  low: '#B3261E',
  overloaded: '#B3261E',
};

function trajectoryColor(value) {
  return TRAJECTORY_COLORS[String(value).toLowerCase()] || '#5F5E5A';
}

function trajectoryLabel(value) {
  if (!value) return '--';
  const s = String(value);
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// ============================================================================
// SHARED PIECES
// ============================================================================

function SectionCard({ title, subtitle, action, children }) {
  return (
    <div className="chart-card">
      <div className="chart-header">
        <div>
          <h3 className="chart-title">{title}</h3>
          {subtitle && <p style={{ margin: '3px 0 0', fontSize: 12, color: '#888780' }}>{subtitle}</p>}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function ViewAllLink({ label = 'View all', onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4, border: 'none', background: 'none',
        cursor: 'pointer', color: '#0F4C75', fontSize: 12.5, fontWeight: 600, padding: 0,
      }}
    >
      {label}
      <ChevronRight size={14} />
    </button>
  );
}

// ============================================================================
// SECTIONS
// ============================================================================

function HealthOverviewCard({ snapshot, onNavigate }) {
  // snapshot === null means no health records yet: an honest empty state, not an error.
  if (!snapshot) {
    return (
      <SectionCard title="Health overview" action={<ViewAllLink onClick={() => onNavigate('Health')} />}>
        <p className="muted-text">No health data recorded yet.</p>
      </SectionCard>
    );
  }

  // Any metric without a reading shows "--" instead of "null".
  const stats = [
    {
      label: 'Heart rate',
      value: snapshot.heartRate != null ? `${snapshot.heartRate} bpm` : '--',
      icon: HeartPulse,
      color: '#D85A30',
    },
    {
      label: 'HRV',
      value: snapshot.hrv != null ? `${snapshot.hrv} ms` : '--',
      icon: HeartPulse,
      color: '#2A9D8F',
    },
    {
      label: 'Blood pressure',
      value: snapshot.bloodPressure ? `${snapshot.bloodPressure} mmHg` : '--',
      icon: HeartPulse,
      color: '#B3261E',
    },
    {
      label: 'Movement',
      value: snapshot.movement != null ? `${snapshot.movement.toLocaleString()} steps` : '--',
      icon: Watch,
      color: '#2A9D8F',
    },
  ];

  return (
    <SectionCard
      title="Health overview"
      subtitle={`${snapshot.coverage}% data coverage this week`}
      action={<ViewAllLink onClick={() => onNavigate('Health')} />}
    >
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10 }}>
        {stats.map(stat => {
          const Icon = stat.icon;
          return (
            <div key={stat.label} style={{ border: '1px solid #EEF0F2', borderRadius: 10, padding: 12 }}>
              <span
                style={{
                  width: 30, height: 30, borderRadius: 8, display: 'inline-flex', alignItems: 'center',
                  justifyContent: 'center', backgroundColor: `${stat.color}1A`, color: stat.color, marginBottom: 8,
                }}
              >
                <Icon size={16} />
              </span>
              <p style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>{stat.value}</p>
              <p style={{ margin: '2px 0 0', fontSize: 11, color: '#888780' }}>{stat.label}</p>
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
}

// Renders the backend-provided summary: trajectory as three status cards,
// then positives / watchouts, then a fixed disclaimer line.
function HealthTrendSummaryCard({ summary, hasData }) {
  // Follows the overview card's data state (hasData) so a trajectory with
  // zero real readings behind it is never shown.
  if (!summary || !hasData) {
    return (
      <SectionCard title="Health trend summary">
        <p className="muted-text">No summary found — add health records to see this.</p>
      </SectionCard>
    );
  }

  const trajectory = summary?.trajectory || {};
  const positives = summary?.positives || [];
  const watchouts = summary?.watchouts || [];

  const categories = [
    { key: 'regulation', label: 'Regulation', value: trajectory.regulation },
    { key: 'repair', label: 'Recovery', value: trajectory.repair },
    { key: 'integration', label: 'Daily Integration', value: trajectory.integration },
  ];

  return (
    <SectionCard title="Health trend summary">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 16 }}>
        {categories.map(cat => (
          <div key={cat.key} style={{ border: '1px solid #EEF0F2', borderRadius: 10, padding: 14 }}>
            <p style={{ margin: 0, fontSize: 12.5, color: '#888780' }}>{cat.label}</p>
            <p style={{ margin: '4px 0 0', fontSize: 15, fontWeight: 700, color: trajectoryColor(cat.value) }}>
              {trajectoryLabel(cat.value)}
            </p>
          </div>
        ))}
      </div>

      {(positives.length > 0 || watchouts.length > 0) && (
        <div style={{ display: 'grid', gap: 8, marginBottom: 14 }}>
          {positives.map((text, i) => (
            <div key={`pos-${i}`} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <ShieldCheck size={15} color="#0F6E56" style={{ flexShrink: 0, marginTop: 1 }} />
              <p style={{ margin: 0, fontSize: 13, color: '#3A3A38' }}>{text}</p>
            </div>
          ))}
          {watchouts.map((text, i) => (
            <div key={`watch-${i}`} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <AlertTriangle size={15} color="#B4780A" style={{ flexShrink: 0, marginTop: 1 }} />
              <p style={{ margin: 0, fontSize: 13, color: '#3A3A38' }}>{text}</p>
            </div>
          ))}
        </div>
      )}

      <p style={{ margin: 0, fontSize: 11.5, color: '#888780' }}>
        This dashboard displays wearable trends and is not a medical diagnosis.
      </p>
    </SectionCard>
  );
}

// Checklist for the user's goal. With no goal set, shows a prompt that
// takes the user to their profile to choose one.
function FamilyPlanningProgressCard({ goal, steps, onNavigate, onSetGoal }) {
  const doneCount = steps.filter(s => s.done).length;
  const percent = steps.length ? Math.round((doneCount / steps.length) * 100) : 0;

  return (
    <SectionCard
      title="Family planning progress"
      subtitle={GOAL_LABELS[goal] || 'Your journey'}
      action={<ViewAllLink label="Manage" onClick={() => onNavigate('Continuouscare')} />}
    >
      {!goal && (
        <div style={{ padding: 10, borderRadius: 8, background: '#0F4C750D', marginBottom: 12, fontSize: 12.5, color: '#3A3A38' }}>
          Choose your family planning goal so we can tailor your steps and features.{' '}
          <button
            onClick={onSetGoal}
            style={{
              border: 'none', background: 'none', padding: 0, color: '#0F4C75',
              fontWeight: 600, cursor: 'pointer', textDecoration: 'underline', fontSize: 12.5,
            }}
          >
            Set my goal
          </button>
        </div>
      )}

      <div style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#888780', marginBottom: 6 }}>
          <span>{doneCount} of {steps.length} complete</span>
          <span>{percent}%</span>
        </div>
        <div style={{ height: 8, borderRadius: 999, background: '#EEF0F2', overflow: 'hidden' }}>
          <div style={{ width: `${percent}%`, height: '100%', background: '#0F4C75', borderRadius: 999 }} />
        </div>
      </div>

      <div style={{ display: 'grid', gap: 8 }}>
        {steps.map(step => (
          <div key={step.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {step.done ? (
              <CheckCircle2 size={16} color="#0F6E56" style={{ flexShrink: 0 }} />
            ) : (
              <Circle size={16} color="#C9CCD1" style={{ flexShrink: 0 }} />
            )}
            <span style={{ fontSize: 13, color: step.done ? '#888780' : '#3A3A38', textDecoration: step.done ? 'line-through' : 'none' }}>
              {step.label}
            </span>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

function UpcomingAppointmentsCard({ appointments, onNavigate }) {
  return (
    <SectionCard title="Upcoming appointments" action={<ViewAllLink onClick={() => onNavigate('Appointment')} />}>
      {appointments.length === 0 ? (
        <p className="muted-text">No upcoming appointments.</p>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {appointments.map(appt => (
            <div key={appt.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span
                style={{
                  width: 34, height: 34, borderRadius: 9, background: '#0F4C7514', color: '#0F4C75',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}
              >
                <Stethoscope size={16} />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: 13.5, fontWeight: 600 }}>{appt.doctor}</p>
                <p style={{ margin: '1px 0 0', fontSize: 12, color: '#888780' }}>{appt.specialty}</p>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <p style={{ margin: 0, fontSize: 12.5, fontWeight: 600, color: '#3A3A38' }}>{appt.date}</p>
                <p style={{ margin: '1px 0 0', fontSize: 11.5, color: '#888780', display: 'flex', alignItems: 'center', gap: 3, justifyContent: 'flex-end' }}>
                  <Clock size={11} /> {appt.time}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}

function RecentReportsCard({ reports, onNavigate }) {
  return (
    <SectionCard title="Recent reports" action={<ViewAllLink onClick={() => onNavigate('Health')} />}>
      {reports.length === 0 ? (
        <p className="muted-text">No reports uploaded yet.</p>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {reports.map(r => (
            <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span
                style={{
                  width: 32, height: 32, borderRadius: 8, background: '#8B5CF614', color: '#8B5CF6',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}
              >
                <FileText size={15} />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</p>
                <p style={{ margin: '1px 0 0', fontSize: 11.5, color: '#888780' }}>{r.date} · {r.size}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}

function RemindersCard({ reminders, onNavigate }) {
  const upcoming = reminders.filter(r => !r.is_done).slice(0, 4);

  return (
    <SectionCard title="Reminders" action={<ViewAllLink label="Manage" onClick={() => onNavigate('Continuouscare')} />}>
      {upcoming.length === 0 ? (
        <p className="muted-text">No upcoming reminders.</p>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {upcoming.map(r => (
            <div key={r.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <span
                style={{
                  width: 28, height: 28, borderRadius: 8, backgroundColor: '#B4780A1A', color: '#B4780A',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1,
                }}
              >
                <Bell size={14} />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: 13, color: '#3A3A38' }}>{r.title}</p>
                <p style={{ margin: '2px 0 0', fontSize: 11, color: '#888780' }}>{formatReminderDateTime(r.remind_at)}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

function navigateTopLevel(tab) {
  const paths = {
    Health: '/health',
    Appointment: '/appointment',
    Continuouscare: '/continuouscare',
  };
  if (paths[tab]) {
    window.location.href = paths[tab];
  }
}

const DashboardTab = ({ onChangeTab }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userInfo, token } = useSelector((state) => state.user);
  const currentUserId = userInfo?.id;

  // Clears the session and sends the user straight to /login.
  const forceLogin = useCallback(() => {
    dispatch(clearUser()); // also wipes token/userInfo from local + session storage
    navigate('/login', { replace: true });
  }, [dispatch, navigate]);
  const goal = userInfo?.family_planning_goal || null;

  // No dummy data: empty accounts, expired tokens and backend errors all
  // show their own honest "no data" / error message.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [progressSteps, setProgressSteps] = useState(() => defaultStepsForGoal(goal));
  const [appointments, setAppointments] = useState([]);
  const [healthSnapshot, setHealthSnapshot] = useState(null);
  const [healthTrendSummary, setHealthTrendSummary] = useState(null);
  const [recentReports, setRecentReports] = useState([]);
  const [reminders, setReminders] = useState([]);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/api/dashboard', {
        params: { user_id: currentUserId },
      });
      const d = response.data?.data || {};

      setError(null);

      setNotifications(d.notifications || []);

      setProgressSteps(
        d.milestones?.length
          ? d.milestones.map(m => ({ id: m.id, label: m.title, done: m.status === 'completed' }))
          : defaultStepsForGoal(goal)
      );

      const confirmedAppts = (d.appointments || [])
        .filter(a => a.status === 'confirmed' && isAppointmentUpcoming(a))
        .sort((a, b) => new Date(`${a.appointment_date}T00:00:00`) - new Date(`${b.appointment_date}T00:00:00`));
      setAppointments(
        confirmedAppts.slice(0, 3).map(a => ({
          id: a.id,
          doctor: a.doctor_name,
          specialty: a.doctor_specialty,
          date: new Date(String(a.appointment_date).slice(0, 10) + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
          time: a.appointment_time,
        }))
      );

      // ---- Health overview + trend summary (same source as the Health page) ----
      // Cap at the last 7 days even if the backend returns 8.
      const rows = (d.health?.rows || []).slice(-7);
      const summary = d.health?.summary;
      const trends = summary?.trends || {};

      // Prefer summary.trends (what the Health page uses), fall back to the
      // most recent non-null value in the rows.
      const hr = num(trends.heart_rate?.latest) ?? latestMetric(rows, 'heart_rate')?.value ?? null;
      const hrv = num(trends.hrv?.latest) ?? latestMetric(rows, 'hrv')?.value ?? null;
      const mv = num(trends.movement?.latest) ?? latestMetric(rows, 'movement')?.value ?? null;
      const bp = latestMetric(rows, 'blood_pressure'); // not part of summary.trends

      const hasAny = hr !== null || hrv !== null || mv !== null || bp !== null;

      setHealthSnapshot(
        hasAny
          ? {
              heartRate: hr !== null ? Math.round(hr) : null,
              hrv: hrv !== null ? Number(hrv.toFixed(1)) : null,
              bloodPressure: bp
                ? `${Math.round(bp.value)}/${bp.row.blood_pressure_diastolic != null ? Math.round(bp.row.blood_pressure_diastolic) : '--'}`
                : null,
              movement: mv,
              coverage: summary?.overallCoverage ?? 0,
            }
          : null
      );

      setHealthTrendSummary(summary && summary.trajectory ? summary : null);

      setRecentReports(
        (d.reports || []).slice(0, 2).map(r => ({
          id: r.id,
          name: r.original_name,
          date: new Date(r.uploaded_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
          size: formatFileSize(r.size_bytes),
        }))
      );

      setReminders(d.reminders || []);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);

      // Token expired and the silent refresh in axiosInstance didn't recover
      // it: go straight to /login instead of showing an error card.
      const status = error.response?.status;
      if (status === 401 || status === 403) {
        forceLogin();
        return;
      }

      setError(error.response?.data?.message || error.message || 'Could not load your dashboard.');
      setNotifications([]);
      setProgressSteps(defaultStepsForGoal(goal));
      setAppointments([]);
      setHealthSnapshot(null);
      setHealthTrendSummary(null);
      setRecentReports([]);
      setReminders([]);
    } finally {
      setLoading(false);
    }
  }, [currentUserId, token, goal, forceLogin]);

  useEffect(() => {
    if (currentUserId) {
      fetchDashboard();
    } else {
      // No signed-in user (session cleared or never started): go to /login.
      setLoading(false);
      navigate('/login', { replace: true });
    }
  }, [currentUserId, fetchDashboard, navigate]);

  const handleNavigate = (tab) => {
    if (tab === 'profile' || tab === 'photos' || tab === 'settings' || tab === 'more' || tab === 'dash') {
      onChangeTab(tab);
    } else {
      navigateTopLevel(tab);
    }
  };

  return (
    <div className="tab-content" id="mainTabContent">
      <div className="tab-pane fade show active p-4" id="dash" role="tabpanel" aria-labelledby="dash-tab">
        <div className="container-xl py-4">
          <DashProfile onEditClick={() => onChangeTab('profile')} notifications={notifications}>
            <div className="section-stack">

              {error && (
                <div className="chart-card" style={{ borderColor: '#B3261E', backgroundColor: '#B3261E0D' }}>
                  <p style={{ margin: 0, fontSize: 13, color: '#B3261E' }}>{error}</p>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 14 }}>
                <FamilyPlanningProgressCard
                  goal={goal}
                  steps={progressSteps}
                  onNavigate={handleNavigate}
                  onSetGoal={() => onChangeTab('profile')}
                />
                <HealthOverviewCard snapshot={healthSnapshot} onNavigate={handleNavigate} />
              </div>

              {/* Health trend summary — full width */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 14 }}>
                <HealthTrendSummaryCard summary={healthTrendSummary} hasData={!!healthSnapshot} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 14 }}>
                <UpcomingAppointmentsCard appointments={appointments} onNavigate={handleNavigate} />
                <RemindersCard reminders={reminders} onNavigate={handleNavigate} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 14 }}>
                <RecentReportsCard reports={recentReports} onNavigate={handleNavigate} />
              </div>
            </div>
          </DashProfile>
        </div>
      </div>
    </div>
  );
};

export default DashboardTab;